import React from "react";
import PropTypes from "prop-types";
import { Button, Modal } from "antd";
import { UserSwitch } from "@phosphor-icons/react";
import { InboxModalHint } from "../AIPane/AIPaneStyled";

/**
 * "Remove from Chris Stratton's mailbox?" — the second confirmation
 * Matthew asked for (2026-10-08) when someone covering another person's
 * inbox marks one of that person's letters reviewed.
 *
 * Three answers, because the question is about the other person's inbox,
 * not the reader's:
 *
 *   Remove from Chris's mailbox  — reviewed for both (the backend default)
 *   Keep in Chris's mailbox      — reviewed for me only; Chris still sees it
 *   Cancel / Escape / X          — nothing happens
 *
 * UI-only: the parent (InboxReviewConfirmProvider) owns the promise.
 */

const joinNames = (names) => {
  const list = names.filter(Boolean);
  if (list.length <= 1) return list[0] || "";
  if (list.length === 2) return `${list[0]} and ${list[1]}`;
  return `${list.slice(0, -1).join(", ")}, and ${list[list.length - 1]}`;
};

// "Chris's", not "Chris'" — the modern American form, and the one that
// reads unambiguously in a button.
const possessive = (name) => `${name}'s`;

export const removeFromMailboxCopy = ({ names = [], count = 1 } = {}) => {
  const people = [...new Set(names.filter(Boolean))];
  const plural = people.length > 1;
  const who = joinNames(people);
  const mailbox = plural ? "mailboxes" : "mailbox";
  const subject = count > 1 ? `these ${count} letters` : "this letter";
  const whose = possessive(who);
  const first = people[0] ? people[0].split(" ")[0] : "";
  return {
    title: `Remove from ${whose} ${mailbox}?`,
    body:
      `${count > 1 ? "These letters are" : "This letter is"} in your inbox ` +
      `because you cover ${whose} mail. Marking ${subject} reviewed can also ` +
      `take ${count > 1 ? "them" : "it"} out of ${whose} ${mailbox}.`,
    remove: plural ? "Remove from their mailboxes" : `Remove from ${possessive(first)} mailbox`,
    keep: plural ? "Keep in their mailboxes" : `Keep in ${possessive(first)} mailbox`,
  };
};

const RemoveFromMailboxModal = ({
  open,
  names = [],
  count = 1,
  onRemove,
  onKeep,
  onCancel,
}) => {
  const copy = removeFromMailboxCopy({ names, count });
  return (
    <Modal
      open={open}
      onCancel={onCancel}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <UserSwitch size={16} weight="bold" />
          {copy.title}
        </span>
      }
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="keep" onClick={onKeep}>
          {copy.keep}
        </Button>,
        <Button key="remove" type="primary" onClick={onRemove}>
          {copy.remove}
        </Button>,
      ]}
      destroyOnClose
    >
      <InboxModalHint style={{ fontSize: 13 }}>{copy.body}</InboxModalHint>
      <InboxModalHint style={{ marginTop: 12 }}>
        Either way {count > 1 ? "they leave" : "it leaves"} your own inbox.
      </InboxModalHint>
    </Modal>
  );
};

RemoveFromMailboxModal.propTypes = {
  open: PropTypes.bool.isRequired,
  names: PropTypes.arrayOf(PropTypes.string),
  count: PropTypes.number,
  onRemove: PropTypes.func.isRequired,
  onKeep: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default RemoveFromMailboxModal;
